<template>
  <v-row no-gutters>
    <v-col cols="12" class="d-flex mb-6">
      <div
        :class="{
          hasSecondLevel: !isFirstLevel,
          isThirdLevel: isThirdLevel,
        }"
        class="header__title"
      >
        <!-- back -->
        <base-btn
          v-if="!isFirstLevel"
          :disabled="loading"
          class="header__title--back"
          type="tertiary"
          icon="mdi-arrow-left"
          color="neutral-light"
          @click="handleBack"
        />

        <!-- main title -->
        <h3 class="header__title--main" @click="handleTitle">Locais</h3>

        <!-- subtitle::first level -->
        <div
          v-if="!isFirstLevel"
          class="header__title--sub d-flex align-center"
        >
          <v-icon>mdi-chevron-right</v-icon>
          <h4 class="text-truncate" @click="handleBack({ isSub: true })">
            {{ this.filters.country }}
          </h4>
        </div>

        <!-- subtitle::second level -->
        <div
          v-if="isThirdLevel"
          class="header__title--sub2 d-flex align-center"
        >
          <v-icon>mdi-chevron-right</v-icon>
          <h4 class="text-truncate">{{ this.filters.region }}</h4>
        </div>
      </div>
    </v-col>

    <v-card v-if="noInfoFound" width="1000px" height="300px" flat>
      <empty-state />
    </v-card>
    <v-row no-gutters justify="center">
      <v-card
        flat
        v-if="loading"
        class="d-flex align-center justify-center px-3"
        height="273px"
        min-width="866px"
      >
        <loading-logo :value="200" />
      </v-card>
      <v-col cols="12" v-else>
        <v-row
          v-if="errors.fetchError"
          class="flex-column"
          justify="center"
          align="center"
          no-gutters
          style="min-height: 250px"
        >
          <v-card flat height="30" class="mb-4">
            <span class="footerText align-text d-block">
              {{ errors.shownErrMsg }}
              <v-tooltip top>
                <template v-slot:activator="{ on, attrs }">
                  <v-icon size="18" v-bind="attrs" v-on="on">
                    mdi-help-circle
                  </v-icon>
                </template>
                <v-container class="text-body-2 pa-0" style="max-width: 230px">
                  {{ errors.tooltipErrMsg }}
                </v-container>
              </v-tooltip>
            </span>
          </v-card>
          <v-btn color="primary" class="ml-3" @click="fetchData">{{
            $t('message.pages.analytics.tryAgain')
          }}</v-btn>
        </v-row>
        <v-row v-else no-gutters align="center">
          <v-card flat>
            <span>{{ $t('message.pages.analytics.byViews') }}</span>
          </v-card>
          <v-col cols="12" class="mt-5">
            <v-row no-gutters align="start">
              <v-card flat width="285px">
                <vue-apex-charts
                  style="position: relative; right: 30px"
                  width="300"
                  type="donut"
                  :options="options"
                  :series="series"
                />
              </v-card>
              <v-col class="mt-3">
                <v-row>
                  <v-col
                    v-for="(locale, i) in chartData"
                    :key="i"
                    :cols="isMobileBreakpoint ? 6 : 4"
                    class="mb-3"
                  >
                    <div class="grid">
                      <div class="grid__flag">
                        <v-icon
                          v-if="!isFirstLevel || locale.name.length !== 2"
                        >
                          mdi-map-marker
                        </v-icon>
                        <country-flag
                          v-else
                          :country="locale.name.toLocaleLowerCase()"
                          size="normal"
                        />
                      </div>
                      <div class="grid__text">
                        <span
                          class="devicesText"
                          :style="`color: ${locale.color};`"
                        >
                          {{ Number(locale.view).toLocaleString() }}
                        </span>
                        <small class="devicesPercentageText">
                          {{ locale.percentage }}%
                        </small>
                      </div>
                      <div class="grid__name">
                        <span class="devicesPercentageText">
                          <i>{{ locale.name }}</i>
                        </span>
                      </div>
                    </div>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
          </v-col>
        </v-row>
      </v-col>
      <v-col v-if="loading" cols="12" class="mt-14">
        <v-skeleton-loader type="table-tbody" />
      </v-col>
      <template v-else-if="!errors.fetchError">
        <v-col cols="12" class="mt-14" style="width: 100%">
          <v-card height="48px" width="100%" flat class="d-flex align-center">
            <v-row
              no-gutters
              class="subtitle-2"
              style="
                font-size: 12px !important;
                font-weight: bold;
                color: var(--v-blueMarine-base);
                padding-left: 15px;
              "
            >
              <v-col>
                <span>{{ columnName[filterLevel] }}</span>
              </v-col>
              <v-col class="d-flex justify-end">
                <span>{{ $t('message.pages.analytics.account.views') }}</span>
              </v-col>
              <v-col class="d-flex justify-end">
                <span>{{
                  $t('message.pages.analytics.account.uniqueViews')
                }}</span>
              </v-col>
              <v-col class="d-flex justify-end">
                <span>{{ $t('message.pages.analytics.account.plays') }}</span>
              </v-col>
              <v-col class="d-flex justify-end pr-2">
                <span>{{
                  $t('message.pages.analytics.account.uniquePlays')
                }}</span>
              </v-col>
            </v-row>
          </v-card>
        </v-col>
        <v-col
          cols="12"
          style="overflow-y: auto"
          :style="`height: ${tableHeight}`"
        >
          <v-card
            v-for="(rowData, i) in allTableData"
            :key="i"
            height="48px"
            width="100%"
            flat
            style="border-top: 1px solid #e6e8f8; border-radius: 0px"
            :class="{ isThirdLevel }"
            class="d-flex align-center data-table__item"
            @click="handleSelectFilter(rowData)"
          >
            <v-row no-gutters class="body-2" style="padding-left: 15px">
              <v-col>
                <span>{{ rowData.name }}</span>
              </v-col>
              <v-col class="d-flex justify-end">
                <span>{{ Number(rowData.view).toLocaleString() }}</span>
              </v-col>
              <v-col class="d-flex justify-end">
                <span>{{ Number(rowData.unique_view).toLocaleString() }}</span>
              </v-col>
              <v-col class="d-flex justify-end">
                <span>{{ Number(rowData.play).toLocaleString() }}</span>
              </v-col>
              <v-col class="d-flex justify-end pr-2">
                <span>{{ Number(rowData.unique_play).toLocaleString() }}</span>
              </v-col>
            </v-row>
          </v-card>
        </v-col>
      </template>
    </v-row>
  </v-row>
</template>

<script>
// external imports
import { format } from 'date-fns'
import { mapState } from 'vuex'
import VueApexCharts from 'vue-apexcharts'
import CountryFlag from 'vue-country-flag'

// internal imports
import i18n from '@/translations/lang'

// API
import newAnalyticsApi from '@/services/newAnalyticsApi'

// Components
import LoadingLogo from '@/components/LoadingLogo.vue'
import EmptyState from './EmptyState.vue'

// Others

export default {
  name: 'Locales',
  components: {
    LoadingLogo,
    VueApexCharts,
    CountryFlag,
    EmptyState,
    BaseBtn: () => import('@/components/BaseBtn.vue'),
  },
  props: {
    componentData: {
      type: Object,
      default: () => ({ selectedVideos: [], dateRange: [] }),
      required: true,
    },
  },
  data: () => ({
    loading: false,
    filters: {
      country: null,
      region: null,
    },
    filterLevel: 'country',
    columnName: {
      country: 'País',
      region: 'Região',
      city: 'Cidade',
    },
    allTableData: [],
    series: [],
    options: {
      colors: ['#9C27B0', '#FF9800', '#E91E63'],
      tooltip: {
        enabled: false,
      },
      dataLabels: {
        enabled: false,
      },
      legend: {
        show: false,
      },
      donut: {
        labels: {
          show: false,
        },
        total: {
          show: false,
          showAlways: false,
        },
      },
    },
    chartData: [],
    colors: [],
    noInfoFound: false,
    errors: {
      fetchError: false,
      shownErrMsg: '',
      tooltipErrMsg: '',
    },
  }),
  computed: {
    ...mapState({
      isMobileBreakpoint: (state) => state.user.isMobileBreakpoint,
    }),
    isFirstLevel() {
      return this.filterLevel === 'country'
    },
    isSecondLevel() {
      return this.filterLevel === 'region'
    },
    isThirdLevel() {
      return this.filterLevel === 'city'
    },
    subtitle() {
      if (this.isSecondLevel) return this.filters.country
      return this.filters.region
    },
    tableHeight() {
      const length = this.allTableData.length
      if (length < 5) {
        return 267
      } else {
        return length * 48 + 5
      }
    },
    selectedVideos() {
      const { selectedVideos } = this.componentData
      return selectedVideos
    },
    dateRange() {
      const { dateRange } = this.componentData
      return dateRange
    },
  },
  watch: {
    async filterLevel() {
      await this.fetchData()
    },
    componentData: {
      handler() {
        this.fetchData()
      },
      deep: true,
      immediate: true,
    },
  },
  methods: {
    async fetchData() {
      this.noInfoFound = false
      this.allTableData = []
      this.chartData = []
      this.series = []
      this.colors = ['#4A148C', '#10859E', '#00695C', '#9E9D24', '#EF6C00']
      this.loading = true
      await this.getLocaleInfo()
      this.loading = false
    },
    async getLocaleInfoFromSelectedVideos() {
      const summedData = []
      const searchAndSumVideo = async (videoId) => {
        const fetchDataFromFilter = {
          country: async () => {
            const { data } = await newAnalyticsApi.getCountryMetricsFromVideo(
              videoId,
              format(this.dateRange[0], 'yyyy-MM-dd'),
              format(this.dateRange[1], 'yyyy-MM-dd')
            )
            const finalData = Object.keys(data).reduce((acc, key) => {
              const summedData = Object.values(data[key]).reduce(
                (acc, item) => {
                  acc.view += item.view
                  acc.unique_view += item.unique_view
                  acc.play += item.play
                  acc.unique_play += item.unique_play
                  return acc
                },
                {
                  view: 0,
                  unique_view: 0,
                  play: 0,
                  unique_play: 0,
                }
              )
              acc.push({
                name: key,
                ...summedData,
              })
              return acc
            }, [])
            return finalData
          },
          region: async () => {
            const ret = await newAnalyticsApi.getRegionMetricsFromVideo(
              videoId,
              format(this.dateRange[0], 'yyyy-MM-dd'),
              format(this.dateRange[1], 'yyyy-MM-dd'),
              this.filters.country
            )
            const regionsData = Object.keys(
              ret.data[this.filters.country]
            ).reduce((acc, key) => {
              acc.push({
                name: key,
                ...ret.data[this.filters.country][key],
              })
              return acc
            }, [])

            return regionsData
          },
          city: async () => {
            const ret = await newAnalyticsApi.getCityMetricsFromVideo(
              videoId,
              format(this.dateRange[0], 'yyyy-MM-dd'),
              format(this.dateRange[1], 'yyyy-MM-dd'),
              this.filters.country,
              this.filters.region
            )
            const citiesData = Object.keys(
              ret.data[this.filters.country][this.filters.region]
            ).reduce((acc, key) => {
              acc.push({
                name: key,
                ...ret.data[this.filters.country][this.filters.region][key]
                  .total,
                ...ret.data[this.filters.country][this.filters.region][key],
              })
              return acc
            }, [])

            return citiesData
          },
        }
        const data = await fetchDataFromFilter[this.filterLevel]()
        for (const item of data) {
          const exists = summedData.find((i) => i.name === item.name)
          if (exists) {
            exists.view += item.view
            exists.unique_view += item.unique_view
            exists.play += item.play
            exists.unique_play += item.unique_play
          } else {
            summedData.push(item)
          }
        }
      }
      let videosToSearch = this.selectedVideos.map((item) => item.id)
      const makeRequestWorker = async () => {
        while (videosToSearch.length) {
          const video = videosToSearch.pop()
          if (!video) return
          await searchAndSumVideo(video)
        }
      }
      const MAX_CONCURRENT_REQUESTS = 5
      const promises = []
      for (let i = 0; i < MAX_CONCURRENT_REQUESTS; i++) {
        promises.push(makeRequestWorker())
      }
      await Promise.all(promises)
      this.filterApiData(summedData)
    },
    async getLocaleInfo() {
      if (this.selectedVideos.length > 0) {
        await this.getLocaleInfoFromSelectedVideos()
        return
      }
      try {
        const fetchDataFromFilter = {
          country: async () => {
            const { data } = await newAnalyticsApi.getCountryMetrics(
              format(this.dateRange[0], 'yyyy-MM-dd'),
              format(this.dateRange[1], 'yyyy-MM-dd')
            )
            const finalData = Object.keys(data).reduce((acc, key) => {
              const summedData = Object.values(data[key]).reduce(
                (acc, item) => {
                  acc.view += item.view
                  acc.unique_view += item.unique_view
                  acc.play += item.play
                  acc.unique_play += item.unique_play
                  return acc
                },
                {
                  view: 0,
                  unique_view: 0,
                  play: 0,
                  unique_play: 0,
                }
              )
              acc.push({
                name: key,
                ...summedData,
              })
              return acc
            }, [])
            return finalData
          },
          region: async () => {
            const ret = await newAnalyticsApi.getRegionMetrics(
              format(this.dateRange[0], 'yyyy-MM-dd'),
              format(this.dateRange[1], 'yyyy-MM-dd'),
              this.filters.country
            )
            const regionsData = Object.keys(
              ret.data[this.filters.country]
            ).reduce((acc, key) => {
              acc.push({
                name: key,
                ...ret.data[this.filters.country][key],
              })
              return acc
            }, [])

            return regionsData
          },
          city: async () => {
            const ret = await newAnalyticsApi.getCityMetrics(
              format(this.dateRange[0], 'yyyy-MM-dd'),
              format(this.dateRange[1], 'yyyy-MM-dd'),
              this.filters.country,
              this.filters.region
            )
            const citiesData = Object.keys(
              ret.data[this.filters.country][this.filters.region]
            ).reduce((acc, key) => {
              acc.push({
                name: key,
                ...ret.data[this.filters.country][this.filters.region][key]
                  .total,
                ...ret.data[this.filters.country][this.filters.region][key],
              })
              return acc
            }, [])

            return citiesData
          },
        }
        const data = await fetchDataFromFilter[this.filterLevel]()
        this.filterApiData(data)
        if (!this.chartData.length) this.noInfoFound = true
      } catch (e) {
        this.handleFetchError(e)
      }
    },
    filterApiData(data) {
      const totalViews = data.reduce((acc, item) => {
        acc += item.view
        return acc
      }, 0)
      this.allTableData = data.sort((a, b) => b.view - a.view)

      this.chartData = []

      let graphColors = []
      let percentages = []
      let sortedData = Object.entries(this.allTableData)
        .map((item) => {
          return {
            name: item[0],
            ...item[1],
          }
        })
        .sort((a, b) => b.view - a.view)
      let othersObject = {
        name: 'Others',
        play: 0,
        unique_play: 0,
        unique_view: 0,
        view: 0,
        color: '#3e50f7',
        percentage: 0,
      }
      for (let i = 0; i < sortedData.length; i++) {
        const maxShown = 8
        if (i < maxShown) {
          let percentage = Number(
            ((100 * sortedData[i].view) / totalViews).toFixed(2)
          )
          percentages.push(percentage)
          let randomColor = this.colors[0] ?? '#3e50f7'
          graphColors.push(randomColor)
          this.colors.shift()
          this.chartData.push({
            color: randomColor,
            percentage: percentage,
            ...sortedData[i],
          })
        } else {
          othersObject.play += sortedData[i].play ?? 0
          othersObject.unique_play += sortedData[i].unique_play ?? 0
          othersObject.unique_view += sortedData[i].unique_view ?? 0
          othersObject.view += sortedData[i].view ?? 0
        }
      }
      if (othersObject.view > 0) {
        othersObject.percentage = Number(
          ((100 * othersObject.view) / totalViews).toFixed(2)
        )
        percentages.push(othersObject.percentage)
        graphColors.push(othersObject.color)
        this.chartData.push(othersObject)
      }
      this.options.colors = graphColors
      this.series = percentages
    },
    handleSelectFilter(rowData) {
      if (this.loading || this.isThirdLevel) return

      let level = 'city'

      this.filters[this.filterLevel] = rowData.name

      if (this.isFirstLevel) level = 'region'

      this.filterLevel = level
    },
    handleBack({ isSub = false } = {}) {
      if (this.loading || (isSub && !this.isThirdLevel)) return

      let level = 'region'

      if (this.isSecondLevel) level = 'country'

      this.filterLevel = level
    },
    handleTitle() {
      if (this.loading || this.isFirstLevel) return

      this.filters.country = null
      this.filters.region = null
      this.filterLevel = 'country'
    },
    handleFetchError(err) {
      this.errors.fetchError = true
      this.errors.tooltipErrMsg =
        err.msg ??
        err.response.data.errMsg ??
        err.response.data.message ??
        'Error'
      this.errors.shownErrMsg = i18n.t('message.errors.unexpectedError')
    },
    clearErrors() {
      Object.keys(this.errors).forEach((e) => {
        if (typeof this.errors[e] === 'string') {
          return (this.errors[e] = '')
        }
        this.errors[e] = false
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.header {
  &__title {
    display: grid;

    grid-template-areas: 'main';
    grid-template-columns: auto;
    grid-template-rows: auto;

    align-items: center;

    &.hasSecondLevel {
      grid-template-areas: 'back main sub';
      grid-template-columns: repeat(3, auto);

      [class*='--main'] {
        cursor: pointer;
      }
    }

    &.isThirdLevel {
      grid-template-areas: 'back main sub sub2';
      grid-template-columns: repeat(4, auto);

      [class*='--main'],
      [class*='--sub']:not([class*='--sub2']) {
        cursor: pointer;
      }
    }

    &--sub h4,
    &--sub2 h4 {
      font: {
        family: Rubik;
        style: normal;
        weight: 500;
        size: 20px;
      }
      line-height: 120%;
      color: var(--v-brand-darken2);
    }

    &--back {
      grid-area: back;
      margin-right: $spacing-plus-1;
      cursor: pointer;

      .v-icon {
        color: var(--v-neutral-lighten1);
      }
    }

    &--main {
      grid-area: main;
    }

    &--sub,
    &--sub2 {
      .v-icon {
        margin: 0 $spacing-minus-2;
        color: var(--v-neutral-lighten2);
      }

      h4 {
        max-width: 180px;
      }
    }

    &--sub {
      grid-area: sub;
    }

    &--sub2 {
      grid-area: sub2;
    }
  }
}

.grid {
  display: grid;
  grid-template-areas:
    'flag text'
    'flag name';
  grid-template-columns: auto 1fr;
  grid-template-rows: 30px auto;

  column-gap: $spacing-base;

  &__flag,
  &__name {
    display: flex;
    align-items: flex-start;
    justify-content: flex-start;
  }

  &__flag {
    grid-area: flag;

    > span {
      transform-origin: top;
      margin: {
        top: 0.4em;
        left: -0.8em;
      }
    }
  }
  &__text {
    grid-area: text;

    display: grid;
    grid-template-columns: auto auto;
    align-items: flex-start;
    justify-content: flex-start;

    column-gap: $spacing-minus-2;

    .devicesPercentageText {
      line-height: 1.35rem;
    }
  }
  &__name {
    grid-area: name;
  }
}

.v-card {
  &.isThirdLevel {
    cursor: initial;

    &:focus::before {
      opacity: 0;
    }

    &::v-deep {
      .v-ripple__container {
        display: none;
      }
    }
  }
}
</style>
